import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield } from 'lucide-react';

const easeOutExpo = [0.16, 1, 0.3, 1];

const publicUrl = process.env.PUBLIC_URL || '';
const COMPLIANCE_BADGES = [
  { src: `${publicUrl}/pci.png`, alt: 'PCI DSS compliant' },
  { src: `${publicUrl}/ssl.png`, alt: '256-bit SSL encryption' },
];

const TransactWorldPreloader = ({ onComplete, duration = 3000 }) => {
  const [progress, setProgress] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const statusMessages = [
    'ESTABLISHING SECURE CONNECTION...',
    'VERIFYING ENCRYPTION...',
    'INITIALIZING GATEWAY...',
    'SYSTEM READY',
  ];

  useEffect(() => {
    let rafId;
    let cancelled = false;
    const startTime = Date.now();

    const updateProgress = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);

      setProgress(newProgress);

      if (newProgress < 100) {
        rafId = requestAnimationFrame(updateProgress);
      } else if (!cancelled) {
        setIsExiting(true);
      }
    };

    rafId = requestAnimationFrame(updateProgress);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [duration]);

  useEffect(() => {
    const messageThresholds = [0, 25, 55, 85];
    for (let i = messageThresholds.length - 1; i >= 0; i -= 1) {
      if (progress >= messageThresholds[i]) {
        setStatusIndex(i);
        break;
      }
    }
  }, [progress]);

  return (
    <AnimatePresence onExitComplete={() => onCompleteRef.current?.()}>
      {!isExiting && (
        <motion.div
          key="tw-preloader"
          data-testid="preloader-container"
          className="preloader-container"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            y: '-100%',
            transition: { duration: 0.6, ease: easeOutExpo },
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#FFFFFF',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(ellipse at 50% 30%, rgba(139,92,246,0.08) 0%, rgba(236,72,153,0.04) 30%, transparent 60%)',
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `
                linear-gradient(rgba(139,92,246,0.03) 1px, transparent 1px),
                linear-gradient(90deg, rgba(139,92,246,0.03) 1px, transparent 1px)
              `,
              backgroundSize: '48px 48px',
              opacity: 0.6,
            }}
          />

          <div
            style={{
              position: 'relative',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '3rem',
              maxWidth: 'min(560px, 94vw)',
              width: '100%',
              padding: '0 2rem',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15, ease: easeOutExpo }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1.125rem',
              }}
            >
              <motion.img
                src="https://customer-assets.emergentagent.com/job_boxes-new/artifacts/cbaiea2t_TWUKLogo.png"
                alt="TransactWorld"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: easeOutExpo }}
                style={{
                  width: 'min(340px, 88vw)',
                  height: 'auto',
                  maxHeight: '88px',
                  objectFit: 'contain',
                }}
              />
              <p
                style={{
                  fontFamily: 'Outfit, system-ui, sans-serif',
                  fontSize: '0.9375rem',
                  fontWeight: '400',
                  letterSpacing: '0.25em',
                  color: '#6B7280',
                  margin: 0,
                  textTransform: 'uppercase',
                }}
              >
                Global Payment Solutions
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                minHeight: '34px',
              }}
            >
              <Shield
                size={22}
                style={{
                  color: '#8B5CF6',
                  filter: 'drop-shadow(0 0 4px rgba(139,92,246,0.4))',
                }}
              />
              <AnimatePresence mode="wait">
                <motion.span
                  key={statusIndex}
                  data-testid="preloader-status-text"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    letterSpacing: '0.12em',
                    color: '#8B5CF6',
                    textTransform: 'uppercase',
                  }}
                >
                  {statusMessages[statusIndex]}
                </motion.span>
              </AnimatePresence>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scaleX: 0.8 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.6, ease: easeOutExpo }}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(139,92,246,0.1)',
                  borderRadius: '999px',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <motion.div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #8B5CF6, #A855F7, #EC4899)',
                    borderRadius: '999px',
                    boxShadow:
                      '0 0 15px rgba(139,92,246,0.4), 0 0 30px rgba(236,72,153,0.2)',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
                    fontSize: '0.875rem',
                    color: '#9CA3AF',
                    letterSpacing: '0.1em',
                  }}
                >
                  LOADING
                </span>
                <span
                  style={{
                    fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    letterSpacing: '0.05em',
                  }}
                >
                  {Math.round(progress)}%
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              style={{
                display: 'flex',
                gap: '1.5rem',
                marginTop: '0.75rem',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {COMPLIANCE_BADGES.map(({ src, alt }) => (
                <img
                  key={src}
                  src={src}
                  alt={alt}
                  draggable={false}
                  style={{
                    height: 'clamp(36px, 10vw, 52px)',
                    width: 'auto',
                    maxWidth: 'min(140px, 42vw)',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ))}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TransactWorldPreloader;
